import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
	Button,
	Checkbox,
	Col,
	Form,
	message,
	Modal,
	Row,
	Select,
	Spin,
	Table,
} from "antd";
import { uniqBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ReactComponent as Edit } from "../assets/Edit_Pencil_01.svg";

import COLORS from "common/Colors";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import EmployeeRoleChangeModal from "../reusableComponent/EmployeeRoleChangeModal";
import NewRolesAndPermissions from "../../src/pages/NewRolesAndPermissions";
import { CALL_API, CALL_FEATCH_API, FETCH_FULL_USER_LIST } from "common/API";
import { getClientid, getLocations, getGradeList } from "redux/selectors";
import { setUserList } from "redux/actions";

const DetailsWrapper = styled.div`
  .change-view {
	color: #2d53da;
	border-radius: 10px;
  }
  .change-view:disabled {
	color: #e3e8fc !important;
  }
  .change-view:hover {
	background: #c5cff5;
  }
  .underline-btn {
	text-decoration: underline !important;
  }
`;

const StyledTable = styled(Table)`
  table {
	table-layout: auto !important;
  }
  .ant-table-content {
	overflow-x: auto;
  }
  .ant-table-cell {
	padding: 10px;
	height: 50px !important;
	border: none;
  }
  .ant-table-thead .ant-table-cell {
	font-weight: bold;
	color: ${COLORS.SECONDARY_BLACK};
  }
  .ant-table-tbody > tr > td {
	border-bottom: 1px solid #f0f0f0 !important;
  }
  .ant-table-tbody > tr:hover .action-icons {
	display: inline-flex;
	gap: 8px;
	cursor: pointer;
  }
  .action-icons {
	display: none;
	cursor: pointer;
  }
`;

const actionButtonStyle = {
	color: "#030303ff",
	background: "#C2FAE5",
	paddingInline: "5px",
	borderRadius: "5px",
};

const RoleDetails = ({
	details,
	menuList = [],
	setMenuList,
	saveMenuList,
	MenuList,
	selectedRoleId,
	viewOnly = false,
	toggleEditRole,
	getRoleList,
	allGradesFromRoles
}) => {
	// viewOnly comes from RoleManagement: true without edit on Settings -> Permission
	const canEdit = !viewOnly;
	const clientId = useSelector(getClientid);
	const locationList = useSelector(getLocations);
	const gradeListData = useSelector(getGradeList) || [];
	const Grade = gradeListData.map((item) => item.jobgradecode);

	const [form] = Form.useForm();
	const [employeeList, setEmployeeList] = useState([]);
	const [filteredList, setFilteredList] = useState([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [loading, setLoading] = useState(false);
	const [bulkMode, setBulkMode] = useState(false);
	const [roleChangePopup, setRoleChangePopup] = useState(false);
	const [roleChangeUserId, setRoleChangeUserId] = useState("");
	const [roleData, setRoleData] = useState("");
	const [isView, setIsView] = useState(false);
	const [isEditPermission, setIsEditPermission] = useState(false);
	const [isCustomPermission, setIsCustomPermission] = useState(false);
	const [editRecord, setEditRecord] = useState({});
	const [showRevertConfirm, setShowRevertConfirm] = useState(false);
	const [editMenuList, setEditMenuList] = useState([]);
	const [gradeModalVisible, setGradeModalVisible] = useState(false);
	const [availableGrades, setAvailableGrades] = useState([]);
	const [selectedGrades, setSelectedGrades] = useState([]);
	const [originalGrades, setOriginalGrades] = useState([]);
	const [gradeLoading, setGradeLoading] = useState(false);
	const [isEditingGrades, setIsEditingGrades] = useState(false);

	const dispatch = useDispatch();
	const history = useHistory();

	// ✅ NEW: additionalrole value passed as query param to get-users-by-role.
	// Change this line if the value comes from somewhere else (prop / redux / other field).
	const additionalRole = details?.additionalrole;

	// ✅ NEW: builds the get-users-by-role URL once, so all 3 calls stay consistent.
	// roleId stays in the path; userid and additionalrole go in the query string.
	const buildUsersByRoleUrl = (userid) => {
		const params = new URLSearchParams();
		if (userid) params.append("userid", userid);
		// additionalrole comes from the owner-roles response (true/false per role),
		// so send it whenever it's defined, including false
		if (additionalRole !== undefined && additionalRole !== null) {
			params.append("additionalrole", String(additionalRole));
		}
		const qs = params.toString();
		return `get-users-by-role/${clientId}/${selectedRoleId}${qs ? `?${qs}` : ""}`;
	};

	useEffect(() => {
		fetchEmployeeDetails();
	}, []);

	const handleOpenGradeModal = () => {
		setOriginalGrades([...selectedGrades]);
		setIsEditingGrades(false);   // ← always start in read-only mode
		setGradeModalVisible(true);
	};
	const sortGrades = (grades = []) =>
		Grade.filter((g) => grades.includes(g));

	// Re-derive this role's own grades from `details` whenever `details` itself
	// changes (role switched, grade master list loads, or the parent resyncs
	// `RoleDetails` after a save triggers getRoleList()/refetch()).
	useEffect(() => {
		const selected = Array.isArray(details?.jobgrade) ? details.jobgrade : [];
		setSelectedGrades(sortGrades(selected));
	}, [details, selectedRoleId, gradeListData]);

	// Recompute which grades are still available (not taken by other roles)
	// whenever other roles' allocations change or this role's own selection
	// changes — always allowing this role to keep the grades it already owns.
	useEffect(() => {
		const allocatedByOtherRoles = allGradesFromRoles
			?.filter((r) => r.roleId !== selectedRoleId)
			?.flatMap((r) => r.jobgrade || []) || [];

		const available = Grade.filter((g) => {
			const takenByOther = allocatedByOtherRoles.includes(g);
			const ownedByThis = selectedGrades.includes(g);
			return !takenByOther || ownedByThis; // block others, always allow own
		});

		setAvailableGrades(sortGrades(available));
	}, [allGradesFromRoles, selectedRoleId, gradeListData, selectedGrades]);

	const fetchEmployeeDetails = async () => {
		try {
			setLoading(true);
			const response = await CALL_FEATCH_API(
				buildUsersByRoleUrl(), // ✅ CHANGED
				"get"
			);

			const apiData = response?.response?.response || [];

			setEmployeeList(apiData);
			setFilteredList(apiData);

			//   // ✅ IMPORTANT: extract jobgrade from API root
			//   const jobgrade = response?.response?.jobgrade || [];
			//   setAvailableGrades(jobgrade);
		} catch (error) {
			message.error("Failed to fetch employee details");
			setEmployeeList([]);
			setFilteredList([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchMasterData = async () => {
		const { code, userList } = await FETCH_FULL_USER_LIST(clientId);

		if (code === "SUCCESS" && userList) {
			dispatch(setUserList(userList));
		}
	};

	const handleFormChange = () => {
		const values = form.getFieldsValue();
		const { name = [], location = [], type = [], country = [] } = values;

		let result = isCustomPermission
			? employeeList.filter((emp) => emp?.custompermission)
			: [...employeeList];

		if (name?.length > 0) {
			result = result.filter((emp) => name.includes(emp.userid));
		}
		if (location?.length > 0) {
			result = result.filter((emp) =>
				location.includes(
					locationList?.find((loc) => loc.location_id === emp.baselocationid)
						?.location_name
				)
			);
		}
		if (type?.length > 0) {
			result = result.filter((emp) => {
				const empType = emp.custompermission ? "Custom" : "Default";
				return type.includes(empType);
			});
		}
		if (country?.length > 0) {
			result = result.filter((emp) =>
				country.includes(emp.managingoffice?.[0])
			);
		}

		setFilteredList(result);
	};

	const handleChangeRole = (e, record) => {
		e.stopPropagation();
		setRoleChangeUserId(record.userid);
		setRoleChangePopup(true);
		setRoleData(record);
	};

	const handleEditClick = async (e, record) => {
		e.stopPropagation();
		if (!canEdit) return;
		setRoleChangeUserId(record.userid);
		setIsEditPermission(true);

		try {
			setLoading(true);
			const response = await CALL_FEATCH_API(
				buildUsersByRoleUrl(record.userid), // ✅ CHANGED
				"get"
			);

			let data = response?.response?.response;

			if (!Array.isArray(data)) {
				data = data ? [data] : [];
			}

			const fetchedRecord = data[0] || record;
			setEditRecord(fetchedRecord);

			const userMenulist =
				fetchedRecord?.menulist?.[String(selectedRoleId)] || [];
			setEditMenuList(userMenulist);

			// ✅ Don't override employeeList and filteredList
		} catch (error) {
			message.error("Failed to fetch employee details");
			setEditRecord(record);
			setEditMenuList([]);
		} finally {
			setLoading(false);
		}
	};

	const handleClickBack = () => {
		getRoleList();
		toggleEditRole(false);
	};

	const handleRevertConfirm = async () => {
		await onFinish({
			type: "default",
			userIds: selectedRowKeys.filter(Boolean),
		});
		setShowRevertConfirm(false);
	};

	const onFinish = async (values) => {
		try {
			setLoading(true);
			const payload = {
				role_id: selectedRoleId || "",
				userIds:
					bulkMode || values?.type === "default"
						? selectedRowKeys.filter(Boolean)
						: [roleChangeUserId].filter(Boolean),
				type: values?.type || "custom",
			};
			if (payload.type === "custom") {
				payload.menulist = {
					[String(selectedRoleId ?? "")]: values,
				};
			}
			const response = await CALL_FEATCH_API(
				`change-custom-permission/${clientId}`,
				"post",
				payload
			);
			if (response?.status === 200) {
				message.success("Permissions updated successfully!");
				setIsCustomPermission(false);
				setIsEditPermission(false);
				setIsView(false);
				setRoleChangePopup(false);
				fetchEmployeeDetails();
				fetchMasterData();
				setSelectedRowKeys([]);
			} else {
				message.error(response?.message || "Failed to update permissions");
			}
		} catch {
			message.error("Something went wrong while updating permissions");
		} finally {
			setLoading(false);
		}
	};

	const uniqueName = uniqBy(
		(isCustomPermission
			? employeeList.filter((emp) => emp?.custompermission)
			: employeeList
		).map((emp) => ({
			label: emp.fullname,
			value: emp.userid,
		})),
		"value"
	);

	const uniqueCountryName = uniqBy(
		(isCustomPermission
			? employeeList.filter((emp) => emp?.custompermission)
			: employeeList
		)
			.map((emp) => ({
				label: emp.managingoffice?.[0] || "-",
				value: emp.managingoffice?.[0] || "-",
			}))
			.filter((c) => c.value !== "-"),
		"value"
	);

	const uniqueLocationName = uniqBy(
		(isCustomPermission
			? employeeList.filter((emp) => emp?.custompermission)
			: employeeList
		)
			.map((emp) => {
				const location = locationList?.find(
					(loc) => loc.location_id === emp.baselocationid
				);
				return {
					label: location?.location_name || "-",
					value: location?.location_name || "-",
				};
			})
			.filter((loc) => loc.value !== "-"),
		"value"
	);

	const baseColumns = [
		{
			title: "Name",
			dataIndex: "fullname",
			key: "fullname",
			render: (text, record) => (
				<span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
					{!isCustomPermission && record.custompermission && (
						<span
							style={{
								display: "inline-block",
								width: 8,
								height: 8,
								borderRadius: "50%",
								backgroundColor: "red",
							}}
						/>
					)}
					{text}
				</span>
			),
		},
		{ title: "Employee ID", dataIndex: "employeeid", key: "employeeid" },
		{ title: "Job Title", dataIndex: "jobtitle", key: "jobtitle" },
		{
			title: "Country",
			dataIndex: "managingoffice",
			key: "managingoffice",
			render: (val) => val?.[0] || "-",
		},
	];

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			const allUserIds = filteredList.map((emp) => emp.userid);
			setSelectedRowKeys(allUserIds);
		} else {
			setSelectedRowKeys([]);
		}
	};

	const actionColumn = {
		title: bulkMode ? (
			<Checkbox
				checked={
					selectedRowKeys.length === filteredList.length &&
					filteredList.length > 0
				}
				indeterminate={
					selectedRowKeys.length > 0 &&
					selectedRowKeys.length < filteredList.length
				}
				onChange={handleSelectAll}
			>
				Select All
			</Checkbox>
		) : (
			"Actions"
		),
		key: "actions",
		align: "center",
		width: "25%",
		render: (_, record) =>
			bulkMode ? (
				<Checkbox
					checked={selectedRowKeys.includes(record.userid)}
					onClick={(e) => e.stopPropagation()}
					onChange={(e) => {
						setSelectedRowKeys((prev) =>
							e.target.checked
								? [...prev, record.userid]
								: prev.filter((id) => id !== record.userid)
						);
					}}
				/>
			) : (
				<div className="action-icons">
					{/* <span
						style={actionButtonStyle}
						onClick={(e) => {
							e.stopPropagation();
							handleChangeRole(e, record);
						}}
					>
						Change Role
					</span> */}
					{canEdit && (
						<Edit
							style={{ cursor: "pointer", ...actionButtonStyle }}
							onClick={(e) => {
								e.stopPropagation();
								handleEditClick(e, record);
							}}
						/>
					)}
				</div>
			),
	};

	const columns = isCustomPermission
		? baseColumns
		: [...baseColumns, actionColumn];

	const handleRowClick = async (record) => {
		setRoleChangeUserId(record.userid);
		setIsView(true);

		try {
			setLoading(true);
			const response = await CALL_FEATCH_API(
				buildUsersByRoleUrl(record.userid), // ✅ CHANGED
				"get"
			);

			let data = response?.response?.response;

			if (!Array.isArray(data)) {
				data = data ? [data] : [];
			}

			const fetchedRecord = data[0] || record;
			setEditRecord(fetchedRecord);

			const userMenulist =
				fetchedRecord?.menulist?.[String(selectedRoleId)] || [];
			setEditMenuList(userMenulist);
		} catch (error) {
			message.error("Failed to fetch employee details");
			setEditRecord(record);
			setEditMenuList([]);
		} finally {
			setLoading(false);
		}
	};
	const handleSaveGrades = async () => {
		try {
			setGradeLoading(true);

			const oldGrades = originalGrades || [];
			const newGrades = selectedGrades || [];

			// 🔥 ONLY find newly added grades
			const addedGrades = newGrades.filter(
				(g) => !oldGrades.includes(g)
			);

			const payload = {
				clientId,
				roleId: selectedRoleId,
				jobgrade: newGrades,      // ✅ FINAL UPDATED LIST
				newjobgrade: addedGrades, // ✅ ONLY ADDED
			};

			const response = await CALL_FEATCH_API(
				`updaterolejobgrade`,
				"post",
				payload
			);

			if (response?.status === "SUCCESS") {
				message.success("Grades updated successfully");
				setGradeModalVisible(false);
				fetchEmployeeDetails();
				history.push(`/roles`); // Refresh details view
				await getRoleList();
			} else {
				message.error(response?.message || "Failed to update grades");
			}

		} catch (err) {
			message.error("Failed to update grades");
		} finally {
			setGradeLoading(false);
		}
	};

	useEffect(() => {
		form.resetFields();
		handleFormChange();
	}, [isCustomPermission]);

	const allOptions = Grade;

	return (
		<DetailsWrapper>
			<Spin spinning={loading}>
				<div className="d-flex justify-content-between align-items-center">
					<div>
						<label
							className="bg-transparent border-0 mb-3 text-left styled-cursor"
							onClick={handleClickBack}
						>
							Role - Permission <ArrowForwardIosIcon className="px-1" />
						</label>

						{(isCustomPermission || isView || isEditPermission) && (
							<label
								className="bg-transparent border-0 mb-3 text-left styled-cursor"
								onClick={() => {
									setIsEditPermission(false);
									setIsView(false);
									setIsCustomPermission(false);
									setSelectedRowKeys([]);
									setEditMenuList([]);
									setEditRecord({});
									// ✅ Refetch all employees to restore full list
									fetchEmployeeDetails();
								}}
							>
								{details?.roleName} <ArrowForwardIosIcon className="px-1" />
							</label>
						)}
						<b>
							{isCustomPermission
								? "Custom Permission"
								: isView
									? editRecord?.fullname
									: isEditPermission
										? "Editing Permission"
										: details?.roleName}
						</b>
					</div>

					{!isCustomPermission && !isView && !isEditPermission && (
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								alignItems: "center",
								gap: "12px",
								marginBottom: "10px",
							}}
						>
							{/* ✅ Job Grade Capsule */}
							<div
								onClick={handleOpenGradeModal}
								style={{
									display: "inline-flex",
									alignItems: "center",
									border: "1px solid #d9d9d9",
									borderRadius: "12px",
									padding: "4px 6px",
									cursor: "pointer",
									background: "#fff",
									gap: "6px",
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.background = "#fafafa")
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.background = "#fff")
								}
							>
								<span
									style={{
										fontSize: "13px",
										color: "#555",
										padding: "0 6px",
										borderRight: "1px solid #e5e5e5",
									}}
								>
									Job Grade
								</span>

								<div
									style={{ display: "flex", gap: "6px", paddingRight: "4px" }}
								>
									{selectedGrades?.length > 0 ? (
										selectedGrades.map((grade) => (
											<span
												key={grade}
												style={{
													fontSize: "12px",
													background: "#f0f0f0",
													padding: "3px 10px",
													borderRadius: "12px",
													color: "#333",
													fontWeight: 500,
												}}
											>
												{grade}
											</span>
										))
									) : (
										<span
											style={{
												fontSize: "12px",
												color: "#999",
												padding: "0 6px",
											}}
										>
											-
										</span>
									)}
								</div>
							</div>

							{/* ✅ Custom Permission */}
							{!bulkMode && (
								<Button
									type="link"
									className="change-view"
									style={actionButtonStyle}
									onClick={() => {
										setBulkMode(false);
										setIsCustomPermission(true);
										setIsEditPermission(false);
										setIsView(false);
									}}
								>
									Custom Permission
								</Button>
							)}
						</div>
					)}
				</div>
				{!isEditPermission && !isView && (
					<>
						<Form
							form={form}
							layout="vertical"
							className="mt-2"
							onValuesChange={handleFormChange}
						>
							<Row align="middle" justify="space-between" gutter={16}>
								<Col flex="none">
									<label>
										{selectedRowKeys?.length > 0
											? `Selected Employees: ${selectedRowKeys?.length}`
											: `Total Employees: ${filteredList?.length}`}
									</label>
								</Col>

								<Col flex="auto">
									<Row gutter={12} justify="end" align="center ">
										<Col>
											<DropdownCheckBox
												name="name"
												label="People"
												options={uniqueName}
												form={form}
												handleFormChange={handleFormChange}
											/>
										</Col>
										<Col>
											<DropdownCheckBox
												name="country"
												label="Country"
												options={uniqueCountryName}
												form={form}
												handleFormChange={handleFormChange}
											/>
										</Col>
										<Col>
											<DropdownCheckBox
												name="type"
												label="Permission Type"
												options={["Custom", "Default"].map((t) => ({
													label: t,
													value: t,
												}))}
												form={form}
												handleFormChange={handleFormChange}
											/>
										</Col>
										{/* <Col>
											<Button
												type="link"
												disabled={isCustomPermission}
												className="underline-btn"
												onClick={() => setBulkMode(!bulkMode)}
											>
												Bulk Role Change
											</Button>
										</Col> */}
									</Row>
								</Col>
							</Row>
						</Form>

						<StyledTable
							dataSource={filteredList}
							columns={columns}
							rowKey="userid"
							pagination={false}
							rowSelection={
								isCustomPermission
									? { selectedRowKeys, onChange: setSelectedRowKeys }
									: null
							}
							onRow={(record) => {
								if (!isCustomPermission) {
									return {
										onClick: () => handleRowClick(record), // ✅ Use the new function
									};
								}
								return {};
							}}
						/>

						{isCustomPermission && selectedRowKeys?.length > 0 && (
							<div className="d-flex justify-content-end mt-3">
								<Button
									style={actionButtonStyle}
									disabled={selectedRowKeys?.length === 0}
									onClick={() => setShowRevertConfirm(true)}
								>
									Revert To Default Permission
								</Button>
							</div>
						)}
						{bulkMode && selectedRowKeys?.length > 0 && (
							<div className="d-flex justify-content-end gap-3 mt-3">
								<Button
									type="link"
									style={actionButtonStyle}
									onClick={() => {
										setBulkMode(false);
										setSelectedRowKeys([]);
									}}
								>
									Cancel
								</Button>
								<Button
									type="link"
									style={actionButtonStyle}
									disabled={selectedRowKeys?.length === 0}
									onClick={() => setRoleChangePopup(true)}
								>
									Next
								</Button>
							</div>
						)}
						{roleChangePopup && (
							<EmployeeRoleChangeModal
								open={roleChangePopup}
								onClose={() => setRoleChangePopup(false)}
								oldRoleId={selectedRoleId}
								userIds={
									bulkMode
										? selectedRowKeys.filter(Boolean)
										: [roleChangeUserId].filter(Boolean)
								}
								roleData={roleData}
								fetchEmployeeDetails={fetchEmployeeDetails}
								setParentLoading={setLoading}
							/>
						)}
					</>
				)}

				{(isView || isEditPermission) && (
					<NewRolesAndPermissions
						viewOnly={isView}
						menuList={editMenuList}
						onBack={() => {
							setIsView(false);
							setIsEditPermission(false);
							setEditMenuList([]); // ✅ Clear menulist
							fetchEmployeeDetails(); // ✅ Refresh the list
						}}
						onFinish={onFinish}
						loading={loading}
						setIsView={setIsView}
						setIsEditPermission={setIsEditPermission}
						canEdit={canEdit}
						setMenuList={setMenuList}
						saveMenuList={saveMenuList}
						MenuList={MenuList}
						handleCancel={() => {
							setIsView(false);
							setIsEditPermission(false);
							setEditMenuList([]); // ✅ Clear menulist
						}}
					/>
				)}

				<Modal
					open={showRevertConfirm}
					onCancel={() => setShowRevertConfirm(false)}
					centered
					footer={null}
					bodyStyle={{ textAlign: "center", borderRadius: "12px" }}
					style={{ borderRadius: "12px" }}
				>
					<p style={{ marginBottom: 24 }}>
						Are you sure you want to revert the permissions to the default
						permission setup of the role <b>{details?.roleName}</b>?
					</p>

					<div
						style={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}
					>
						<Button
							key="no"
							onClick={() => setShowRevertConfirm(false)}
							style={{
								border: "1px solid #000",
								color: "#000",
								background: "#fff",
								borderRadius: "8px",
								width: "80px",
							}}
						>
							No
						</Button>
						<Button
							key="yes"
							type="primary"
							loading={loading}
							onClick={handleRevertConfirm}
							style={{
								background: "#3DD598",
								borderColor: "#3DD598",
								borderRadius: "8px",
								width: "80px",
							}}
						>
							Yes
						</Button>
					</div>
				</Modal>

				<Modal
					title="Job Grade"
					open={gradeModalVisible}
					centered
					onCancel={() => {
						setGradeModalVisible(false);
						setIsEditingGrades(false);
						setSelectedGrades(originalGrades);
					}}
					footer={
						isEditingGrades ? (
							<div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
								<Button
									onClick={() => {
										setSelectedGrades(originalGrades);
										setIsEditingGrades(false);
									}}
									style={{
										borderRadius: 8,
										border: "1px solid #000",
										color: "#000",
									}}
								>
									Cancel
								</Button>

								<Button
									loading={gradeLoading}
									onClick={handleSaveGrades}
									style={{
										borderRadius: 8,
										background: "#c2fae5",
										borderColor: "#c2fae5",
										color: "#111",
										fontWeight: 600,
									}}
								>
									Update
								</Button>
							</div>
						) : null
					}
				>
					{!isEditingGrades ? (
						/* ── READ-ONLY VIEW ── */
						<div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: 8,
								}}
							>
								<span style={{ fontSize: 13, color: "#666" }}>
									Job Grade that are mapped to this role
								</span>

								{canEdit && (
								<div
									onClick={() => {
										setOriginalGrades(selectedGrades); // ✅ snapshot
										setIsEditingGrades(true);
									}}
									style={{
										cursor: "pointer",
										background: "#c2fae5",
										borderRadius: 8,
										padding: "4px 8px",
										display: "flex",
										alignItems: "center",
									}}
								>
									<Edit style={{ fontSize: 16, color: "#333" }} />
								</div>
								)}
							</div>

							<div
								style={{
									display: "flex",
									gap: 8,
									flexWrap: "wrap",
									marginTop: 12,
								}}
							>
								{selectedGrades?.length > 0 ? (
									selectedGrades.map((grade) => (
										<span
											key={grade}
											style={{
												background: "#f0f0f0",
												borderRadius: 20,
												padding: "4px 16px",
												fontSize: 13,
												fontWeight: 500,
												color: "#333",
											}}
										>
											{grade}
										</span>
									))
								) : (
									<span style={{ color: "#999", fontSize: 13 }}>
										No grades assigned
									</span>
								)}
							</div>
						</div>
					) : (
						/* ── EDIT VIEW ── */
						<div>
							<p style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
								Job Grade that should be mapped to this role
							</p>

							<Select
								mode="multiple"
								value={selectedGrades}
								onChange={(vals) => setSelectedGrades(vals)}
								style={{ width: "100%" }}
								placeholder="Select grades"
								getPopupContainer={(trigger) => trigger.parentNode}   // ✅ FIXED
								optionLabelProp="label"
								maxTagCount="responsive"
								showSearch={false}
								dropdownStyle={{ zIndex: 2000 }}   // ✅ IMPORTANT
								menuItemSelectedIcon={
									<span style={{ color: "#2ecc71", fontWeight: 700 }}>✓</span>
								}
								dropdownRender={(menu) => {
									const isAllSelected =
										availableGrades.length > 0 &&
										selectedGrades.length === availableGrades.length;

									const isIndeterminate =
										selectedGrades.length > 0 &&
										selectedGrades.length < availableGrades.length;

									return (
										<div>
											{/* All + Reset */}
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													padding: "8px 12px",
													borderBottom: "1px solid #f0f0f0",
												}}
											>
												<Checkbox
													checked={isAllSelected}
													indeterminate={isIndeterminate}
													onChange={(e) =>
														setSelectedGrades(
															e.target.checked ? [...availableGrades] : []
														)
													}
												>
													All
												</Checkbox>

												<span
													style={{
														fontSize: 13,
														color: "#1677ff",
														cursor: "pointer",
													}}
													onClick={() => setSelectedGrades([])}
												>
													Reset
												</span>
											</div>

											{menu}
										</div>
									);
								}}
							>
								{availableGrades.map((grade) => (
									<Select.Option key={grade} value={grade} label={grade}>
										{grade}
									</Select.Option>
								))}
							</Select>
						</div>
					)}
				</Modal>
			</Spin>
		</DetailsWrapper>
	);
};

export default RoleDetails;